import React from 'react';
import { useCity } from '../contexts/CityContext';
import { BROSSARD_SECTOR_SHAPES } from '../data/brossard';

/** Brossard's sectors, already projected into the 600×420 viewBox. */
const BROSSARD_VIEW = BROSSARD_SECTOR_SHAPES.map(s => ({
  letter: s.letter,
  points: s.points.map(p => `${p[0]},${p[1]}`).join(' '),
  cx: s.label[0],
  cy: s.label[1]
}));
export interface CollectesDistrict {
  id: string;
  name: string;
  label: string;
  adoption: number; // 0-100
  addresses: number;
  calendars: number;
  reminders: number;
  atnaQuestions: number;
  complaints: number;
  openRate: number;
  points: string;
  labelX: number;
  labelY: number;
}
export const collectesDistricts: CollectesDistrict[] = [{
  id: 'A',
  name: 'Secteur A',
  label: 'A',
  adoption: 82,
  addresses: 8420,
  calendars: 12450,
  reminders: 4120,
  atnaQuestions: 842,
  complaints: 5,
  openRate: 78,
  points: '20,20 200,20 200,160 20,160',
  labelX: 110,
  labelY: 95
}, {
  id: 'B',
  name: 'Secteur B',
  label: 'B',
  adoption: 42,
  addresses: 12480,
  calendars: 8120,
  reminders: 5241,
  atnaQuestions: 1284,
  complaints: 18,
  openRate: 73,
  points: '200,20 400,20 400,160 200,160',
  labelX: 300,
  labelY: 95
}, {
  id: 'C',
  name: 'Secteur C',
  label: 'C',
  adoption: 91,
  addresses: 6240,
  calendars: 9840,
  reminders: 3840,
  atnaQuestions: 412,
  complaints: 2,
  openRate: 84,
  points: '400,20 580,20 580,160 400,160',
  labelX: 490,
  labelY: 95
}, {
  id: 'L',
  name: 'Secteur L',
  label: 'L',
  adoption: 68,
  addresses: 9120,
  calendars: 10240,
  reminders: 4100,
  atnaQuestions: 945,
  complaints: 12,
  openRate: 71,
  points: '20,160 200,160 200,300 20,300',
  labelX: 110,
  labelY: 235
}, {
  id: 'M',
  name: 'Secteur M',
  label: 'M',
  adoption: 74,
  addresses: 11050,
  calendars: 14200,
  reminders: 5800,
  atnaQuestions: 820,
  complaints: 8,
  openRate: 76,
  points: '200,160 400,160 400,300 200,300',
  labelX: 300,
  labelY: 235
}, {
  id: 'O',
  name: 'Secteur O',
  label: 'O',
  adoption: 88,
  addresses: 5400,
  calendars: 8100,
  reminders: 3200,
  atnaQuestions: 310,
  complaints: 3,
  openRate: 81,
  points: '400,160 580,160 580,300 400,300',
  labelX: 490,
  labelY: 235
}, {
  id: 'R',
  name: 'Secteur R',
  label: 'R',
  adoption: 55,
  addresses: 7800,
  calendars: 6400,
  reminders: 2800,
  atnaQuestions: 1102,
  complaints: 15,
  openRate: 68,
  points: '20,300 300,300 300,400 20,400',
  labelX: 160,
  labelY: 355
}, {
  id: 'S',
  name: 'Secteur S',
  label: 'S',
  adoption: 79,
  addresses: 8900,
  calendars: 11500,
  reminders: 4900,
  atnaQuestions: 640,
  complaints: 6,
  openRate: 75,
  points: '300,300 580,300 580,400 300,400',
  labelX: 440,
  labelY: 355
}];
export function getAdoptionStatus(adoption: number): 'critical' | 'warning' | 'on-track' {
  if (adoption < 60) return 'critical';
  if (adoption < 80) return 'warning';
  return 'on-track';
}
export function CollectesHeatMap({
  districts,
  selectedId,
  onSelect
}: {
  districts: CollectesDistrict[];
  selectedId: string;
  onSelect: (id: string) => void;
}) {
  const {
    city
  } = useCity();
  const isBrossard = city === 'Brossard';
  const fillFor = (adoption: number, selected: boolean) => {
    const status = getAdoptionStatus(adoption);
    if (status === 'critical') return selected ? '#DC2626' : '#FCA5A5'; // Red
    if (status === 'warning') return selected ? '#D97706' : '#FCD34D'; // Yellow
    return selected ? '#059669' : '#A7F3D0'; // Green
  };
  return <div className="border border-slate-200 rounded-xl shadow-sm bg-white p-4 flex flex-col gap-3 h-full" data-id="element-1640" data-mp="666lo6z14h1y">
      <div className="flex items-center justify-between" data-id="element-1641" data-mp="2m18k78evuer5">
        <div className="text-xs font-medium text-slate-500 uppercase tracking-wide font-body" data-id="element-1642" data-mp="2qza1pownu60i">
          Carte des secteurs · {city}
        </div>
        <div className="flex items-center gap-3 text-xs text-slate-500 font-body" data-id="element-1643" data-mp="369zx26u5zxdi">
          <div className="flex items-center gap-1.5" data-id="element-1644" data-mp="31fvtzyo0s9hp">
            <span className="w-2.5 h-2.5 rounded-sm bg-[#FCA5A5] border border-red-300" data-id="element-1645" data-mp="2xescz8u8k7ew"></span>
            Faible adoption
          </div>
          <div className="flex items-center gap-1.5" data-id="element-1646" data-mp="2si1vch2s93rd">
            <span className="w-2.5 h-2.5 rounded-sm bg-[#FCD34D] border border-amber-300" data-id="element-1647" data-mp="3p79j7vqvjoyg"></span>
            Adoption moyenne
          </div>
          <div className="flex items-center gap-1.5" data-id="element-1648" data-mp="3l5o03snqyeft">
            <span className="w-2.5 h-2.5 rounded-sm bg-[#A7F3D0] border border-emerald-300" data-id="element-1649" data-mp="3g5jtcucn4y2s"></span>
            Forte adoption
          </div>
        </div>
      </div>
      <svg viewBox="0 0 600 420" className="w-full h-auto mt-auto mb-auto" role="img" aria-label="Carte de chaleur de l'adoption numérique des collectes" data-id="element-1650" data-mp="3b92y7g70xjg7">
        {isBrossard && BROSSARD_VIEW.map(shape => {
        const d = districts.find(x => x.id === shape.letter);
        const selected = d?.id === selectedId;
        return <g key={shape.letter} onClick={d ? () => onSelect(d.id) : undefined} className={d ? 'cursor-pointer' : ''} data-id="element-1651" data-mp="1ukmvbzw82m8f">
                <polygon points={shape.points} fill={d ? fillFor(d.adoption, selected) : '#F1F5F9'} stroke={selected ? '#0F172A' : '#ffffff'} strokeWidth={selected ? 3 : 1.5} strokeLinejoin="round" className="transition-all" data-id="element-1652" data-mp="24evmaj5lu74h" />
                <text x={shape.cx} y={d ? shape.cy - 6 : shape.cy} textAnchor="middle" dominantBaseline="middle" className="pointer-events-none select-none" fill={d ? '#0F172A' : '#94A3B8'} fontSize={d ? 16 : 12} fontWeight="bold" fontFamily="sans-serif" data-id="element-1653" data-mp="2nbwsg7awada0">
                  {shape.letter}
                </text>
                {d && <text x={shape.cx} y={shape.cy + 10} textAnchor="middle" dominantBaseline="middle" className="pointer-events-none select-none" fill="#0F172A" fontSize="10" opacity="0.7" fontFamily="sans-serif" data-id="element-1654" data-mp="uvvkaloi9iks">
                    {d.adoption} %
                  </text>}
              </g>;
      })}
        {!isBrossard && districts.map(d => {
        const selected = d.id === selectedId;
        return <g key={d.id} onClick={() => onSelect(d.id)} className="cursor-pointer group" data-id="element-1655" data-mp="1mjxarp7zrsc0">
              <polygon points={d.points} fill={fillFor(d.adoption, selected)} stroke={selected ? '#0F172A' : '#ffffff'} strokeWidth={selected ? 3 : 2} className="transition-all" data-id="element-1656" data-mp="21a1tn62ya0pm" />
              <text x={d.labelX} y={d.labelY} textAnchor="middle" dominantBaseline="middle" className="pointer-events-none select-none" fill="#0F172A" fontSize="22" fontWeight="bold" fontFamily="sans-serif" data-id="element-1657" data-mp="1x757j5e7bmgx">
                {d.label}
              </text>
              <text x={d.labelX} y={d.labelY + 22} textAnchor="middle" dominantBaseline="middle" className="pointer-events-none select-none" fill="#0F172A" fontSize="12" opacity="0.7" fontFamily="sans-serif" data-id="element-1658" data-mp="1dimn11454x8k">
                {d.adoption} %
              </text>
            </g>;
      })}
      </svg>
    </div>;
}